import EmployeeName from 'components/employee/EmployeeName';
import VacationMarker from 'components/employee/VacationMarker';
import { SearchTermHighlight } from 'components/SearchTermHighlight';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type RecordKind, recordOfLinkPath } from 'utils/globalSearchRoutes';
import { displayName, type NamedEntityParts, useLastNameFirst } from 'utils/nameOrder';
import { RecordChip, type RecordChipProps } from './RecordChip';

export type RecordLinkProps = Omit<RecordChipProps, 'entityType' | 'id' | 'className' | 'hitArea'> & {
    /** Where the link goes in classic; the chip's record too, unless `entityType` names one. */
    to: string;
    /** The chip's record, where `to` is not its own route. Without an `id` there is no chip. */
    entityType?: RecordKind;
    id?: number | null;
    /** Classic draws a plain `<a href>` rather than a router link. */
    anchor?: boolean;
    /** What classic's link holds; the label when there is none. */
    children?: ReactNode;
    /** Classic's link classes; the chip draws its own. */
    className?: string;
    /** The chip's classes, such as its spacing. */
    chipClassName?: string;
    title?: string;
};

/**
 * A link that names a record: in classic the link it always was, a router link (`withOrigin`: a `LinkWithOrigin`) or
 * an `anchor`; in the new design the record's chip, which keeps the link's target, click handler, state and `returnTo`.
 * The record is `to`'s when that is exactly `/<segment>/<id>`, else `entityType` and `id`'s. Without a record or a
 * label, the new design draws classic's link too.
 */
export function RecordLink({
    to,
    entityType,
    id,
    anchor,
    children,
    className,
    chipClassName,
    title,
    label,
    partyNumber,
    search,
    withOrigin,
    hideTitle,
    wrapLabel,
    ...linkProps
}: RecordLinkProps) {
    const isNewDesign = useIsNewDesign();
    const record = entityType ? (id == null ? null : { entityType, id }) : recordOfLinkPath(to);
    // `?.`: a name the API sends as null reaches here typed as a string.
    if (isNewDesign && record && label?.trim()) {
        return (
            <RecordChip
                {...linkProps}
                {...record}
                label={label}
                partyNumber={partyNumber}
                search={search}
                withOrigin={withOrigin}
                hideTitle={hideTitle}
                wrapLabel={wrapLabel}
                hitArea={false}
                className={chipClassName}
            />
        );
    }
    if (anchor) {
        return (
            <a {...linkProps} href={to} className={className} title={title}>
                {children ?? label}
            </a>
        );
    }
    const Anchor = withOrigin ? LinkWithOrigin : Link;
    return (
        <Anchor {...linkProps} to={to} className={className} title={title}>
            {children ?? label}
        </Anchor>
    );
}

/**
 * `<EmployeeName>` as a link to its record, an employee's or any other named one's: in classic inside the link; in the
 * new design its vacation marker, then the chip under the name it shows, as one inline box, so a column host neither
 * splits them onto two lines nor stretches the chip.
 */
export function EmployeeNameLink({
    entity,
    ...linkProps
}: Omit<RecordLinkProps, 'label' | 'children'> & { entity: NamedEntityParts }) {
    const isNewDesign = useIsNewDesign();
    const label = displayName({ ...entity, onVacation: false }, useLastNameFirst());
    if (!isNewDesign) {
        return (
            <RecordLink {...linkProps} label={label}>
                <EmployeeName entity={entity} />
            </RecordLink>
        );
    }
    return (
        <span className="inline-flex max-w-full min-w-0 items-center">
            <VacationMarker onVacation={entity.onVacation} />
            {/* The name as `EmployeeName` draws it, for a link that stays one. */}
            <RecordLink {...linkProps} label={label}>
                <SearchTermHighlight text={label} />
            </RecordLink>
        </span>
    );
}
